export type MarkdownLineKind =
    | "heading"
    | "paragraph"
    | "blockquote"
    | "rich"
    | "table-border"
    | "table-header"
    | "table-row";

export interface MarkdownTextSegment {
    text: string;
    emphasis?: boolean;
}

export interface MarkdownLine {
    kind: MarkdownLineKind;
    text: string;
    segments?: MarkdownTextSegment[];
    compact?: boolean;
}

export interface MarkdownRenderOptions {
    maxChars?: number;
}

const MARKDOWN_HEADING_RE = /^\*\*(.+)\*\*$/;
const MARKDOWN_HASH_HEADING_RE = /^#{1,6}\s+(.+)$/;
const MARKDOWN_BOLD_RE = /\*\*(.+?)\*\*/g;
const TABLE_ROW_RE = /^\s*\|.+\|\s*$/;
const TABLE_SEPARATOR_RE = /^\s*\|[\s:|-]+\|\s*$/;

function parseInlineBold(line: string): MarkdownTextSegment[] {
    const segments: MarkdownTextSegment[] = [];
    let lastIndex = 0;

    for (const match of line.matchAll(MARKDOWN_BOLD_RE)) {
        const index = match.index ?? 0;
        if (index > lastIndex) {
            segments.push({ text: line.slice(lastIndex, index) });
        }
        segments.push({ text: match[1]!, emphasis: true });
        lastIndex = index + match[0].length;
    }

    if (lastIndex < line.length) {
        segments.push({ text: line.slice(lastIndex) });
    }

    return segments.length > 0 ? segments : [{ text: line }];
}

export function classifyMarkdownLine(line: string): MarkdownLine {
    if (!line) {
        return { kind: "paragraph", text: "" };
    }

    const hashHeadingMatch = line.match(MARKDOWN_HASH_HEADING_RE);
    if (hashHeadingMatch) {
        return { kind: "heading", text: hashHeadingMatch[1]! };
    }

    const headingMatch = line.match(MARKDOWN_HEADING_RE);
    if (headingMatch) {
        return { kind: "heading", text: headingMatch[1]! };
    }

    if (line.startsWith("> ")) {
        return { kind: "blockquote", text: line };
    }

    if (line.includes("**")) {
        return { kind: "rich", text: line, segments: parseInlineBold(line) };
    }

    return { kind: "paragraph", text: line };
}

function isTableRow(line: string): boolean {
    return TABLE_ROW_RE.test(line);
}

function isTableSeparator(line: string): boolean {
    return TABLE_SEPARATOR_RE.test(line);
}

function parseTableCells(line: string): string[] {
    return line
        .trim()
        .slice(1, -1)
        .split("|")
        .map((cell) => cell.trim());
}

function stripCellMarkers(cell: string): string {
    return cell.replace(/\*\*(.+?)\*\*/g, "$1").replace(/`/g, "");
}

function columnWidths(rows: string[][]): number[] {
    const widths: number[] = [];
    for (const row of rows) {
        for (let i = 0; i < row.length; i++) {
            const width = stripCellMarkers(row[i]!).length;
            widths[i] = Math.max(widths[i] ?? 0, width);
        }
    }
    return widths;
}

function tableCharWidth(widths: number[]): number {
    return widths.reduce((sum, width) => sum + width, 0) + 3 * widths.length + 1;
}

const MIN_TABLE_COL_WIDTH = 3;

function truncateCell(text: string, width: number): string {
    if (width <= 0) return "";
    if (text.length <= width) return text;
    if (width <= 3) return text.slice(0, width);
    return `${text.slice(0, width - 3)}...`;
}

function fitColumnWidths(rows: string[][], maxChars: number): number[] {
    const natural = columnWidths(rows);
    if (tableCharWidth(natural) <= maxChars) {
        return natural;
    }

    const colCount = natural.length;
    const budget = maxChars - (3 * colCount + 1);
    const minBudget = colCount * MIN_TABLE_COL_WIDTH;

    if (budget <= minBudget) {
        return natural.map(() => MIN_TABLE_COL_WIDTH);
    }

    const naturalSum = natural.reduce((sum, width) => sum + width, 0) || 1;
    const widths = natural.map((width) =>
        Math.max(MIN_TABLE_COL_WIDTH, Math.floor((budget * width) / naturalSum)),
    );

    while (tableCharWidth(widths) > maxChars) {
        const widest = Math.max(...widths);
        const index = widths.findIndex((width) => width === widest);
        if (index < 0 || widths[index]! <= MIN_TABLE_COL_WIDTH) {
            break;
        }
        widths[index]!--;
    }

    return widths;
}

function formatTableBorder(widths: number[]): string {
    const segments = widths.map((width) => "-".repeat(width + 2));
    return `+${segments.join("+")}+`;
}

function formatTableRow(cells: string[], widths: number[]): string {
    const padded = widths.map((width, index) => {
        const text = truncateCell(stripCellMarkers(cells[index] ?? ""), width);
        return ` ${text.padEnd(width)} `;
    });
    return `|${padded.join("|")}|`;
}

export function parseMarkdownTableBlock(
    tableLines: string[],
): { header: string[]; rows: string[][] } | null {
    return parseTableBlock(tableLines);
}

function parseTableBlock(tableLines: string[]): { header: string[]; rows: string[][] } | null {
    if (tableLines.length < 2 || !isTableSeparator(tableLines[1]!)) {
        return null;
    }

    return {
        header: parseTableCells(tableLines[0]!),
        rows: tableLines.slice(2).map(parseTableCells),
    };
}

export function isMarkdownTableRow(line: string): boolean {
    return isTableRow(line);
}

export function renderMarkdownTable(
    header: string[],
    rows: string[][],
    options: MarkdownRenderOptions = {},
): MarkdownLine[] {
    return tableToLines(header, rows, options.maxChars);
}

function tableToLines(header: string[], rows: string[][], maxChars?: number): MarkdownLine[] {
    const allRows = [header, ...rows];
    const widths = maxChars ? fitColumnWidths(allRows, maxChars) : columnWidths(allRows);
    const line = (kind: MarkdownLineKind, text: string): MarkdownLine => ({ kind, text, compact: true });

    const lines: MarkdownLine[] = [
        line("table-border", formatTableBorder(widths)),
        line("table-header", formatTableRow(header, widths)),
        line("table-border", formatTableBorder(widths)),
    ];

    for (const row of rows) {
        lines.push(line("table-row", formatTableRow(row, widths)));
    }

    lines.push(line("table-border", formatTableBorder(widths)));
    return lines;
}

export function renderMarkdownLines(
    lines: string[],
    options: MarkdownRenderOptions = {},
): MarkdownLine[] {
    const rendered: MarkdownLine[] = [];

    for (let index = 0; index < lines.length; ) {
        const line = lines[index]!;

        if (!line.trim()) {
            index++;
            continue;
        }

        if (isTableRow(line)) {
            const tableLines: string[] = [];
            while (index < lines.length && isTableRow(lines[index]!)) {
                tableLines.push(lines[index]!);
                index++;
            }

            const table = parseTableBlock(tableLines);
            if (table) {
                rendered.push(...tableToLines(table.header, table.rows, options.maxChars));
                continue;
            }

            for (const tableLine of tableLines) {
                rendered.push(classifyMarkdownLine(tableLine));
            }
            continue;
        }

        rendered.push(classifyMarkdownLine(line));
        index++;
    }

    return rendered;
}
